import { cloneDeep } from "lodash";
import {
  GLP1_PROGRAM,
  POINTS_WORKSHOP_PROGRAM,
  POINTS_PROGRAM,
  DIABETES_WORKSHOP_PROGRAM,
  DIABETES_PROGRAM,
  CLINICAL_PROGRAM,
  CORE_PRODUCT,
  PREMIUM_PRODUCT
} from "../../utils/resultFunctions/constants";

import {
  setCookieValue,
  getCookieValue,
  SECONDS_IN_THIRTY_DAYS
} from "../../utils/cookies";
import { RESULTS_ELI_WW, RESULTS_ELI_SEQUENCE } from "../../utils/eligibility";
import {
  WHY_LOSE_WEIGHT,
  MULTI_CHOICE_GROUPS,
  DIETARY,
  COACHING_STYLE,
  START_POINTS_BRANCH,
  START_POINTS_WORKSHOP_BRANCH
} from "../../config/tags";
import { isUserOnProgramBranch } from "../../utils/uniBranching";

export const saveResultsCookie = ({ data, baseUrl }) => {
  const cookie = getCookieValue("ww_assessment", true) || {};

  cookie[`wwQuizResults_${baseUrl}`] = data;

  setCookieValue("ww_assessment", cookie, SECONDS_IN_THIRTY_DAYS);
};

export const loadResultsCookie = () => {
  const cookie = getCookieValue("ww_assessment", true);

  return cookie;
};

export const loadResultsCookieByBaseUrl = baseUrl => {
  const cookie = loadResultsCookie();

  if (!cookie) {
    return null;
  }

  return cookie[`wwQuizResults_${baseUrl}`];
};

const addYouWantToBlockProfileData = ({ data, profile }) => {
  data.profile.imperial = cloneDeep(profile.imperial);
  data.profile.metric = cloneDeep(profile.metric);
  data.profile.measurementSystem = profile.measurementSystem;
};

const addYouWantToBlockAnswersData = ({ data, answers, assessment }) => {
  // The data needed to generate "You want to" block
  if (!data.answers) {
    data.answers = [];
  }
  const whyLoseWeightQuestion = assessment.questions.find(q =>
    q.tags.includes(WHY_LOSE_WEIGHT)
  );
  data.answers.push(
    cloneDeep(answers.find(a => a.questionId === whyLoseWeightQuestion?._id))
  );
};

const addRecipesRecommendationsAnswers = ({ data, answers, questions }) => {
  if (!data.answers) {
    data.answers = [];
  }

  const cuisinesQuestionsIds = questions
    .filter(q => q.tags.includes(MULTI_CHOICE_GROUPS))
    .map(q => q?._id);

  const cuisinesAnswer = answers.find(a =>
    cuisinesQuestionsIds.includes(a.questionId)
  );

  data.answers = [...data.answers, cuisinesAnswer];

  const dietaryQuestionsIds = questions
    .filter(q => q.tags.includes(DIETARY))
    .map(q => q?._id);

  const dietaryAnswer = answers.find(a =>
    dietaryQuestionsIds.includes(a.questionId)
  );

  data.answers = [...data.answers, dietaryAnswer];

  // Currently only for uni V1.
  if (isUserOnProgramBranch(answers, questions)) {
    const questionStartPointsBranch =
      questions.find(q => q.tags.includes(START_POINTS_BRANCH)) || {};

    const questionStartPointsWorkshopBranch =
      questions.find(q => q.tags.includes(START_POINTS_WORKSHOP_BRANCH)) || {};

    const answerVisitedProgramBranch = answers.find(
      a =>
        a.questionId === questionStartPointsBranch._id ||
        a.questionId === questionStartPointsWorkshopBranch._id
    );

    if (answerVisitedProgramBranch) {
      data.answers.push(answerVisitedProgramBranch);
    }
  }
};

const addCoachesRecommendationsAnswers = ({ data, answers, questions }) => {
  if (!data.answers) {
    data.answers = [];
  }

  const coachQuestionIds = questions
    .filter(q => q.tags.includes(COACHING_STYLE))
    .map(q => q?._id);

  if (!questions.length > 0) {
    return;
  }

  const answer = answers.find(a => coachQuestionIds.includes(a.questionId));

  if (answer) {
    data.answers.push(answer);
  }
};

export const saveDefaultResultsData = ({
  assessmentId,
  suggestedProduct,
  assessment,
  answers,
  profile,
  baseUrl
}) => {
  if (!assessmentId) {
    return;
  }

  const data = {
    aid: assessmentId,
    resultsScreen: suggestedProduct,
    suggestedProduct
  };

  // The data needed to generate chart.
  data.profile = {};
  data.profile.gender = profile.gender;

  addYouWantToBlockProfileData({ data, profile });
  addYouWantToBlockAnswersData({ data, answers, assessment });

  addRecipesRecommendationsAnswers({
    data,
    answers,
    questions: assessment.questions
  });
  addCoachesRecommendationsAnswers({
    data,
    answers,
    questions: assessment.questions
  });

  saveResultsCookie({ data, baseUrl });
};

export const saveUniResultsData = ({
  assessmentId,
  answers,
  assessment,
  baseUrl,
  resultsScreen,
  profile
}) => {
  if (!assessmentId) {
    return;
  }

  const data = {
    aid: assessmentId,
    resultsScreen,
    profile: cloneDeep(profile || {})
  };

  if (
    [
      POINTS_WORKSHOP_PROGRAM,
      POINTS_PROGRAM,
      DIABETES_WORKSHOP_PROGRAM,
      DIABETES_PROGRAM,
      CORE_PRODUCT,
      PREMIUM_PRODUCT
    ].includes(resultsScreen)
  ) {
    addRecipesRecommendationsAnswers({
      data,
      answers,
      questions: assessment.questions
    });
    addCoachesRecommendationsAnswers({
      data,
      answers,
      questions: assessment.questions
    });
  }

  if (
    [
      GLP1_PROGRAM,
      POINTS_WORKSHOP_PROGRAM,
      POINTS_PROGRAM,
      DIABETES_WORKSHOP_PROGRAM,
      DIABETES_PROGRAM
    ].includes(resultsScreen)
  ) {
    addYouWantToBlockProfileData({ data, profile });
    addYouWantToBlockAnswersData({ data, answers, assessment });
  }

  if (resultsScreen === CLINICAL_PROGRAM) {
    addYouWantToBlockProfileData({ data, profile });
  }

  saveResultsCookie({ data, baseUrl });
};

export const saveEliResultsData = ({
  assessment,
  assessmentId,
  profile,
  resultsScreen,
  eligibleSequence,
  newQuiz,
  answers,
  baseUrl
}) => {
  if (!assessmentId) {
    return;
  }

  const data = {
    aid: assessmentId,
    resultsScreen,
    eligibleSequence
  };

  if (resultsScreen === RESULTS_ELI_WW && newQuiz.coreUrl) {
    data.coreUrl = newQuiz.coreUrl;
  }

  if (resultsScreen === RESULTS_ELI_SEQUENCE) {
    if (newQuiz.eliUrl) {
      data.eliUrl = newQuiz.eliUrl;
    }
  }

  if ([RESULTS_ELI_WW, RESULTS_ELI_SEQUENCE].includes(resultsScreen)) {
    // The data needed to generate chart.
    data.profile = {};
    data.profile.gender = profile.gender;

    addYouWantToBlockProfileData({ data, profile });
    addYouWantToBlockAnswersData({ data, answers, assessment });
  }

  saveResultsCookie({ data, baseUrl });
};
